import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class PdfRendererService {
  private platformId = inject(PLATFORM_ID);
  
  async renderPage(pdfjsDoc: any, pageNumber: number, canvas: HTMLCanvasElement, targetCssWidth: number): Promise<{ viewport: any; pageSize: { width: number; height: number } }> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('PdfRendererService can only be used in the browser');
    }

    const pdfjsLib = await import('pdfjs-dist');
    
    // Set worker
    if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
    }

    const page = await pdfjsDoc.getPage(pageNumber);
    const unscaledViewport = page.getViewport({ scale: 1.0 });
    
    // Calculate scale to match target width
    const scale = targetCssWidth / unscaledViewport.width;
    const viewport = page.getViewport({ scale });
    
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not get 2d context from canvas');

    // Handle high DPI displays
    const outputScale = window.devicePixelRatio || 1;
    
    canvas.width = Math.floor(viewport.width * outputScale);
    canvas.height = Math.floor(viewport.height * outputScale);
    canvas.style.width = Math.floor(viewport.width) + "px";
    canvas.style.height = Math.floor(viewport.height) + "px";

    const transform = outputScale !== 1
      ? [outputScale, 0, 0, outputScale, 0, 0]
      : undefined;

    const renderContext = {
      canvasContext: context,
      transform: transform,
      viewport: viewport
    };

    await page.render(renderContext).promise;

    return {
      viewport,
      pageSize: { width: viewport.width, height: viewport.height }
    };
  }
  
  async getPdfDocument(bytes: ArrayBuffer) {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('PdfRendererService can only be used in the browser');
    }
    
    const pdfjsLib = await import('pdfjs-dist');
    
    // Set worker
    if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
    }

    const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(bytes) });
    return loadingTask.promise;
  }
}
